import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { useWorkspace } from '../contexts/WorkspaceContext';
import api from '../lib/api';
import { 
  FileText, Trash2, Plus, Loader2, FolderKanban, Calendar,
  CheckSquare, Flag, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import AppLayout from '../components/AppLayout';

const Templates = () => {
  const { workspaces } = useWorkspace();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState('');
  const [selectedProject, setSelectedProject] = useState('');
  const [projects, setProjects] = useState([]);
  const [dueDate, setDueDate] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // Fetch projects when workspace changes
  useEffect(() => {
    const fetchProjects = async () => {
      if (selectedWorkspace) {
        try {
          const response = await api.get(`/workspaces/${selectedWorkspace}/projects`);
          setProjects(response.data.projects || []);
        } catch {
          setProjects([]);
        }
      } else {
        setProjects([]);
      }
      setSelectedProject('');
    };
    fetchProjects();
  }, [selectedWorkspace]);

  const handleDelete = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await api.delete(`/templates/${templateId}`);
      setTemplates(prev => prev.filter(t => t.template_id !== templateId));
      toast.success('Template deleted');
    } catch {
      toast.error('Failed to delete template');
    }
  };

  const openUseDialog = (template) => {
    setSelectedTemplate(template);
    setSelectedWorkspace('');
    setSelectedProject('');
    setDueDate('');
    setUseDialogOpen(true);
  };

  const handleUseTemplate = async () => {
    if (!selectedWorkspace) {
      toast.error('Please select a workspace');
      return;
    }
    
    setCreating(true);
    try {
      const response = await api.post(`/templates/${selectedTemplate.template_id}/use`, {
        workspace_id: selectedWorkspace,
        project_id: selectedProject && selectedProject !== 'none' ? selectedProject : null,
        due_date: dueDate || null
      });
      toast.success(`Task created: ${response.data.title}`);
      setUseDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  const getPriorityColor = (priority) => {
    if (priority === 'high') return 'bg-red-100 text-red-700';
    if (priority === 'medium') return 'bg-yellow-100 text-yellow-700';
    if (priority === 'low') return 'bg-green-100 text-green-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <AppLayout>
      <div className="h-full flex flex-col" data-testid="templates-page">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b bg-white">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-6 h-6 text-[#4A9CC7]" />
                Task Templates
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Save tasks as reusable templates for quick task creation
              </p>
            </div>
          </div>
        </div>

        {/* Templates List */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#4A9CC7] animate-spin" />
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-2">No templates yet</p>
              <p className="text-sm text-gray-400 max-w-md mx-auto">
                Create templates from existing tasks by clicking the "..." menu on any task 
                and selecting "Save as Template"
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map(template => (
                <Card key={template.template_id} className="hover:shadow-md transition-shadow" data-testid={`template-${template.template_id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-base font-medium">{template.name}</CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-gray-400 hover:text-red-500"
                        onClick={() => handleDelete(template.template_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">
                      Created {formatDate(template.created_at)}
                    </p>
                  </CardHeader>
                  <CardContent>
                    {template.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {template.description}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap gap-2 mb-4">
                      {template.source_task?.priority && (
                        <Badge className={`text-xs ${getPriorityColor(template.source_task.priority)}`}>
                          <Flag className="w-3 h-3 mr-1" />
                          {template.source_task.priority}
                        </Badge>
                      )}
                      
                      {template.source_task?.subtasks?.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <CheckSquare className="w-3 h-3 mr-1" />
                          {template.source_task.subtasks.length} subtasks
                        </Badge>
                      )}
                      
                      {template.source_task?.is_recurring && (
                        <Badge variant="outline" className="text-xs text-[#4A9CC7]">
                          Recurring
                        </Badge>
                      )}
                    </div>
                    
                    <Button
                      onClick={() => openUseDialog(template)}
                      className="w-full bg-[#4A9CC7] hover:bg-[#3d8ab5]"
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Use Template
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Use Template Dialog */}
      <Dialog open={useDialogOpen} onOpenChange={setUseDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-[#4A9CC7]" />
              Create Task from Template
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-medium text-sm">{selectedTemplate?.name}</p>
              {selectedTemplate?.source_task?.subtasks?.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  Includes {selectedTemplate.source_task.subtasks.length} subtasks
                </p>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Workspace *</Label>
              <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
                <SelectTrigger data-testid="template-workspace-select">
                  <SelectValue placeholder="Select workspace" />
                </SelectTrigger>
                <SelectContent>
                  {workspaces.map(ws => (
                    <SelectItem key={ws.workspace_id} value={ws.workspace_id}>
                      {ws.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Project (Optional)</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject} disabled={!selectedWorkspace}>
                <SelectTrigger>
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Project</SelectItem>
                  {projects.map(proj => (
                    <SelectItem key={proj.project_id} value={proj.project_id}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: proj.color || '#4A9CC7' }}
                        />
                        {proj.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Due Date (Optional)</Label>
              <Input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              onClick={handleUseTemplate}
              disabled={creating || !selectedWorkspace}
              className="bg-[#4A9CC7] hover:bg-[#3d8ab5]"
            >
              {creating ? 'Creating...' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default Templates;
